import { useCallback, useRef, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Select, Input, Tag, Modal, message } from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined, AppstoreOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  listOpportunities, deleteOpportunity,
  type Opportunity, type OpportunityFilters,
} from '@/api/opportunityApi';
import { listPipelines, listStages, type Pipeline, type PipelineStage } from '@/api/pipelineApi';
import { listCustomers, type Customer } from '@/api/customerApi';
import { useAuth } from '@/hooks/useAuth';

const STATUS_COLORS: Record<string, string> = {
  open: 'blue',
  won: 'green',
  lost: 'red',
  suspended: 'orange',
};

interface SelectOption {
  value: string;
  label: string;
}

export default function OpportunityListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [filters, setFilters] = useState<OpportunityFilters>({});
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [stages, setStages] = useState<PipelineStage[]>([]);

  // Customer searchable filter
  const [customerOptions, setCustomerOptions] = useState<SelectOption[]>([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const customerSearchTimer = useRef<ReturnType<typeof setTimeout>>();

  const canWrite = hasPermission('opportunities', 'write');
  const canDelete = hasPermission('opportunities', 'delete');

  // Load pipelines for filter dropdown
  useEffect(() => {
    listPipelines({ from: 0, size: 100 })
      .then((res) => setPipelines(res.items))
      .catch(() => setPipelines([]));
  }, []);

  // Load stages for all pipelines (for name resolution in table)
  const [allStages, setAllStages] = useState<PipelineStage[]>([]);

  useEffect(() => {
    if (pipelines.length === 0) return;
    Promise.all(pipelines.map((p) => listStages(p.id).catch(() => ({ items: [] as PipelineStage[] }))))
      .then((results) => {
        const combined = results.flatMap((r) => r.items);
        setAllStages(combined);
      });
  }, [pipelines]);

  // Load stages for filter dropdown (scoped to selected pipeline)
  useEffect(() => {
    if (filters.pipeline_id) {
      listStages(filters.pipeline_id)
        .then((res) => setStages(res.items))
        .catch(() => setStages([]));
    } else {
      setStages([]);
    }
  }, [filters.pipeline_id]);

  // Customer search for filter
  const searchCustomers = useCallback((keyword: string) => {
    if (customerSearchTimer.current) clearTimeout(customerSearchTimer.current);
    customerSearchTimer.current = setTimeout(async () => {
      setCustomerSearching(true);
      try {
        const res = await listCustomers({ from: 0, size: 20, keyword: keyword || undefined });
        setCustomerOptions(
          res.items.map((c: Customer) => ({
            value: c.id,
            label: `${c.name}${c.company ? ` (${c.company})` : ''}`,
          }))
        );
      } catch {
        setCustomerOptions([]);
      } finally {
        setCustomerSearching(false);
      }
    }, 300);
  }, []);

  // Load initial customer options
  useEffect(() => {
    searchCustomers('');
    return () => {
      if (customerSearchTimer.current) clearTimeout(customerSearchTimer.current);
    };
  }, [searchCustomers]);

  const handleFilterChange = useCallback(
    (key: keyof OpportunityFilters, value: string | undefined) => {
      setFilters((prev) => {
        const next = { ...prev, [key]: value };
        // Clear stage_id when pipeline changes
        if (key === 'pipeline_id') {
          next.stage_id = undefined;
        }
        return next;
      });
    },
    []
  );

  const fetchData = useCallback(
    (params: SearchParams) => listOpportunities(params, filters),
    [filters]
  );

  const filterMounted = useRef(false);
  useEffect(() => {
    if (!filterMounted.current) {
      filterMounted.current = true;
      return;
    }
    gridRef.current?.reload();
  }, [filters]);

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('opportunities.confirm_delete'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteOpportunity(id);
            message.success(t('opportunities.delete_success'));
            gridRef.current?.reload();
          } catch (err) {
            const msg = err instanceof Error ? err.message : t('app.error');
            message.error(msg);
          }
        },
      });
    },
    [t]
  );

  // Build pipeline name lookup
  const pipelineMap = useMemo(() => {
    const map = new Map<string, string>();
    pipelines.forEach((p) => map.set(p.id, p.name));
    return map;
  }, [pipelines]);

  // Build stage name lookup (from all stages across all pipelines)
  const stageMap = useMemo(() => {
    const map = new Map<string, string>();
    allStages.forEach((s) => map.set(s.id, s.name));
    return map;
  }, [allStages]);

  const columns: ColumnsType<Opportunity> = useMemo(
    () => [
      {
        title: t('opportunities.field.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true,
        render: (name: string, record: Opportunity) => (
          <a onClick={() => navigate(`/opportunities/${record.id}`)}>{name}</a>
        ),
      },
      {
        title: t('opportunities.field.amount'),
        dataIndex: 'amount',
        key: 'amount',
        sorter: true,
        width: 130,
        render: (amount: number, record: Opportunity) =>
          amount > 0
            ? `${record.currency || 'TWD'} ${amount.toLocaleString()}`
            : '-',
      },
      {
        title: t('opportunities.field.probability'),
        dataIndex: 'probability',
        key: 'probability',
        sorter: true,
        width: 90,
        render: (val: number) => `${val}%`,
      },
      {
        title: t('opportunities.field.pipeline_id'),
        dataIndex: 'pipeline_id',
        key: 'pipeline_id',
        ellipsis: true,
        width: 140,
        render: (val: string) => pipelineMap.get(val) || val,
      },
      {
        title: t('opportunities.field.stage_id'),
        dataIndex: 'stage_id',
        key: 'stage_id',
        ellipsis: true,
        width: 120,
        render: (val: string) => stageMap.get(val) || val,
      },
      {
        title: t('opportunities.field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (status: string) => (
          <Tag color={STATUS_COLORS[status] ?? 'default'}>
            {t(`opportunities.status_options.${status}`, status)}
          </Tag>
        ),
      },
      {
        title: t('opportunities.field.close_date'),
        dataIndex: 'close_date',
        key: 'close_date',
        sorter: true,
        width: 120,
        render: (val: string | null) =>
          val ? new Date(val).toLocaleDateString() : '-',
      },
      {
        title: t('opportunities.field.source'),
        dataIndex: 'source',
        key: 'source',
        ellipsis: true,
        width: 100,
      },
      {
        title: t('opportunities.field.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        width: 170,
        render: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
      },
      {
        title: t('app.actions'),
        key: 'actions',
        width: 150,
        render: (_: unknown, record: Opportunity) => (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/opportunities/${record.id}`)}
            />
            {canWrite && (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/opportunities/${record.id}/edit`)}
              />
            )}
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, navigate, canWrite, canDelete, handleDelete, pipelineMap, stageMap]
  );

  const pipelineOptions = useMemo(
    () => pipelines.map((p) => ({ value: p.id, label: p.name })),
    [pipelines]
  );

  const stageOptions = useMemo(
    () => stages.map((s) => ({ value: s.id, label: s.name })),
    [stages]
  );

  const statusOptions = useMemo(
    () => [
      { value: 'open', label: t('opportunities.status_options.open') },
      { value: 'won', label: t('opportunities.status_options.won') },
      { value: 'lost', label: t('opportunities.status_options.lost') },
      { value: 'suspended', label: t('opportunities.status_options.suspended') },
    ],
    [t]
  );

  const filterBar = (
    <Space wrap>
      <Select
        placeholder={t('opportunities.filter.pipeline_id')}
        style={{ width: 160 }}
        allowClear
        options={pipelineOptions}
        onChange={(val) => handleFilterChange('pipeline_id', val || undefined)}
      />
      <Select
        placeholder={t('opportunities.filter.stage_id')}
        style={{ width: 160 }}
        allowClear
        options={stageOptions}
        disabled={!filters.pipeline_id}
        value={filters.stage_id}
        onChange={(val) => handleFilterChange('stage_id', val || undefined)}
      />
      <Select
        placeholder={t('opportunities.filter.status')}
        style={{ width: 120 }}
        allowClear
        options={statusOptions}
        onChange={(val) => handleFilterChange('status', val || undefined)}
      />
      <Input.Search
        placeholder={t('opportunities.filter.owner_id')}
        style={{ width: 140 }}
        allowClear
        onSearch={(val) => handleFilterChange('owner_id', val || undefined)}
        enterButton={false}
      />
      <Select
        showSearch
        filterOption={false}
        placeholder={t('opportunities.filter.customer_id')}
        style={{ width: 180 }}
        allowClear
        options={customerOptions}
        loading={customerSearching}
        onSearch={searchCustomers}
        onChange={(val) => handleFilterChange('customer_id', val || undefined)}
      />
      <Input.Search
        placeholder={t('opportunities.filter.source')}
        style={{ width: 140 }}
        allowClear
        onSearch={(val) => handleFilterChange('source', val || undefined)}
        enterButton={false}
      />
    </Space>
  );

  const toolbar = (
    <Space>
      {filterBar}
      <Button
        icon={<AppstoreOutlined />}
        onClick={() => navigate('/opportunities/kanban')}
      >
        {t('opportunities.kanban_view')}
      </Button>
      {canWrite && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/opportunities/new')}
        >
          {t('opportunities.new_opportunity')}
        </Button>
      )}
    </Space>
  );

  return (
    <div>
      <h2>{t('opportunities.list_title')}</h2>
      <DataGrid<Opportunity>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('opportunities.search_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
        onRow={(record) => ({
          onDoubleClick: () => navigate(`/opportunities/${record.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
}
